//dependencies
import { format } from "date-fns";
import React from "react";

//components
import { Avatar, ICellRendererParams, Link, PYColumnDef, Text, ValueGetterParams } from "@performyard/py-components";

import AvatarGroup from "../../../components/AvatarGroup";

//utils
import toEmployeeUrl from "../../../utils/URLs/Employee";
import { Feedback } from "../../UserFeedback/utils";

const SharedWithTextMap: Record<string, string> = {
	subject: "Subject",
	manager: "Manager",
	all: "Everyone",
	everyone: "Everyone",
	me: "Me",
	"subject-+-manager": "Subject & Subject's Manager",
	"subject-manager": "Subject's Manager",
};

const SubjectCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	const subjects = feedback.subject || [];

	if (subjects.length < 2) {
		return (
			<div className="flex h-full items-center">
				{subjects.map((subject) => (
					<Link key={subject._id} href={toEmployeeUrl(subject._id)} isExternal>
						<div className="flex items-center gap-1">
							<Avatar size={20} id={subject._id} name={subject.name} />
							<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
								{subject.name}
							</Text>
						</div>
					</Link>
				))}
			</div>
		);
	}

	return (
		<div className="flex h-full items-center">
			<AvatarGroup canShowModal employees={subjects} maxLength={5} modalTitle="Feedback Subjects" />
		</div>
	);
};

const AuthorCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	const authors = feedback.author || [];

	if (authors.length < 2) {
		return (
			<div className="flex h-full items-center">
				{authors.map((author) => (
					<Link key={author._id} href={toEmployeeUrl(author._id)} isExternal>
						<div className="flex items-center gap-1">
							<Avatar size={20} id={author._id} name={author.name} />
							<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
								{author.name}
							</Text>
						</div>
					</Link>
				))}
			</div>
		);
	}

	return (
		<div className="flex h-full items-center">
			<AvatarGroup canShowModal employees={authors} maxLength={5} modalTitle="Feedback Authors" />
		</div>
	);
};

const SharedWithCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
				{SharedWithTextMap[feedback.shared_with] || ""}
			</Text>
		</div>
	);
};

const DateCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	const lastUpdated = new Date(feedback.last_updated);

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3">{format(lastUpdated, "MMM d, yyyy")}</Text>
		</div>
	);
};

const ContentCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	return (
		<div className="flex h-full items-center">
			<Link href={`/view-feedback/${feedback._id}`} isExternal>
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{feedback.content || ""}
				</Text>
			</Link>
		</div>
	);
};

const HashtagsCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	return (
		<div className="flex h-full items-center">
			<Text size="caption" dangerouslySetClassName="py-line-clamp-1">
				{feedback.categories?.join(" ") || ""}
			</Text>
		</div>
	);
};

const RequestedCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3">{feedback.response_to_request ? "Yes" : "No"}</Text>
		</div>
	);
};

const CustomFieldValueGetter =
	(customFieldsMap: Record<string, any>, customField: any) => (params: ValueGetterParams<Feedback>) => {
		const matchingCustomField = customFieldsMap[customField._id];
		const type = matchingCustomField?.type || "";

		const feedback = params?.data;
		const subjects = feedback?.subject || [];

		const valuesPerSubject: string[] = [];
		for (const subject of subjects) {
			const rowCustomFieldValues = subject?.custom_field_values[customField._id];
			let displayValue = rowCustomFieldValues?.displayValue ?? String(rowCustomFieldValues?.value ?? "");

			if (type === "date" && displayValue) {
				const date = new Date(displayValue);
				displayValue = format(date, "MMM d, yyyy");
			}

			valuesPerSubject.push(displayValue);
		}

		// 1. if multiple subject multiple value, <value>; <value>
		// 2. if multiple subject single value, <value>; N/A OR N/A; <value>
		// 3. if multiple subject no value, just empty string
		// 4. if single subject, just the value
		if (valuesPerSubject.length < 1) return "";
		if (valuesPerSubject.length === 1) return valuesPerSubject[0];
		let ret = "";
		const hasValue = valuesPerSubject.some((v) => v);
		if (hasValue) {
			for (let i = 0; i < valuesPerSubject.length; i++) {
				const value = valuesPerSubject[i];
				if (value) {
					ret += value;
				} else {
					ret += "N/A";
				}
				if (i < valuesPerSubject.length - 1) {
					ret += "; ";
				}
			}
		}
		return ret;
	};

export const getCompanyFeedbackColumns = (params: {
	customFields: any[];
	enabledCustomFieldIds: string[];
	streaming: boolean;
}): Partial<PYColumnDef<Feedback>>[] => {
	const { customFields = [], enabledCustomFieldIds = [], streaming } = params;

	const baseCols: Partial<PYColumnDef<Feedback>>[] = [
		{
			field: "subject.name",
			headerName: "Subject",
			sortable: true,
			resizable: false,
			...(streaming && { pinned: "left" }),
			cellRenderer: SubjectCellRenderer,
		},
		{
			field: "author.name",
			headerName: "Author",
			sortable: true,
			resizable: false,
			cellRenderer: AuthorCellRenderer,
		},
		{
			field: "shared_with",
			headerName: "Shared With",
			sortable: true,
			resizable: false,
			width: 98,
			cellRenderer: SharedWithCellRenderer,
		},
		{
			field: "last_updated",
			headerName: "Date",
			sortable: true,
			resizable: false,
			width: 100,
			cellRenderer: DateCellRenderer,
		},
		{
			field: "content",
			headerName: "Content",
			sortable: false,
			resizable: false,
			...(streaming ? { width: 200, flex: 0 } : { flex: 1 }),
			cellRenderer: ContentCellRenderer,
		},
		{
			field: "categories",
			headerName: "Hashtags",
			sortable: true,
			resizable: false,
			cellRenderer: HashtagsCellRenderer,
		},
		{
			field: "response_to_request",
			headerName: "Requested",
			sortable: true,
			resizable: false,
			width: 96,
			cellRenderer: RequestedCellRenderer,
		},
	];

	if (customFields.length === 0) return baseCols;

	// Add custom_field columns for all the enabled custom fields
	const customFieldsMap: Record<string, any> = Object.fromEntries(customFields.map((cf: any) => [cf._id, cf]));
	const enabledCustomCols = customFields
		.filter((cf: any) => enabledCustomFieldIds.includes(cf._id))
		.map((cf: any) => ({
			field: "subject.custom_field_values.value",
			headerName: cf.name,
			sortable: true,
			resizable: true,
			filter: true,
			valueGetter: CustomFieldValueGetter(customFieldsMap, cf),
		}));

	return [...baseCols, ...enabledCustomCols];
};
